// Metadata de auditoría que viaja con cada CREATE/EDIT de detalle.
// Formato (el backend la guarda tal cual en CargoDetail.Metadata, nvarchar(300);
// los segmentos se extraen por regex, el orden es libre):
//   "Version:<v>;Cobertura:<c>;Velocidad:<k>Kbps;EditTs:<epochMillis>;<device>"
//
// EditTs es el instante REAL de la edición del usuario, no el del envío. Con la
// cola offline los dos pueden separarse días, y sin este dato la auditoría de
// una carga encolada miente: parece guardada cuando por fin hubo cobertura.
//
// Reglas:
//  - Edición en primer plano → EditTs = ahora (default de buildMetadata).
//  - Item ENCOLADO → la metadata se construye AL GUARDAR y viaja persistida
//    dentro del item. El reintento NUNCA la regenera: falsearía tanto la
//    cobertura (la del momento de subir, no la de la carga) como el instante
//    de la edición.
//  - Items encolados ANTES de que existiera EditTs (metadata sin el token) →
//    el flush lo inyecta con ensureEditTs usando createdAt del item.

export function buildMetadata(editTs: number = Date.now()): string {
  const version = __APP_VERSION__;

  type NavConn = { effectiveType?: string; downlink?: number };
  const conn = (navigator as Navigator & { connection?: NavConn }).connection;

  let cobertura = 'Desconocida';
  let velocidad = '0Kbps';
  if (conn) {
    switch (conn.effectiveType) {
      case 'slow-2g': cobertura = 'Sin señal'; break;
      case '2g':      cobertura = 'Deficiente'; break;
      case '3g':      cobertura = 'Debil'; break;
      case '4g':      cobertura = 'Buena'; break;
    }
    if (conn.downlink != null) velocidad = `${Math.round(conn.downlink * 1000)}Kbps`;
  }

  const ua = navigator.userAgent;
  let device = 'Desconocido';
  if (/iphone/i.test(ua)) {
    const ios = ua.match(/CPU iPhone OS ([\d_]+)/)?.[1]?.replace(/_/g, '.');
    device = ios ? `iPhone-iOS${ios}` : 'iPhone';
  } else if (/ipad/i.test(ua)) {
    const ios = ua.match(/CPU OS ([\d_]+)/)?.[1]?.replace(/_/g, '.');
    device = ios ? `iPad-iOS${ios}` : 'iPad';
  } else {
    const android = ua.match(/Android[^;]*;\s*([^)]+)\)/);
    if (android) device = android[1].trim().replace(/\s+/g, '');
    else if (/windows/i.test(ua)) device = 'Windows';
    else if (/macintosh/i.test(ua)) device = 'Mac';
  }

  return `Version:${version};Cobertura:${cobertura};Velocidad:${velocidad};EditTs:${editTs};${device}`;
}

// Garantiza que una metadata lleva el token EditTs. Para items encolados antes
// de que existiera (o encolados por caminos que no pasan por buildMetadata),
// inyecta el fallback — típicamente item.createdAt, el instante en que el
// usuario guardó. NUNCA sustituye un EditTs existente.
//
// Función pura y sin DOM a propósito: la comparte el Service Worker, que no
// tiene acceso a `navigator.userAgent` con el mismo significado ni debe
// reconstruir metadata de un item ajeno.
export function ensureEditTs(metadata: string | undefined, fallbackTs: number): string {
  if (metadata && /EditTs:\d+/.test(metadata)) return metadata;
  if (metadata) return `${metadata};EditTs:${fallbackTs}`;
  return `EditTs:${fallbackTs}`;
}
